import type {
  CatchUpTimelineEvent,
  CatchUpTimelineSummary,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/activity'

export const pluralize = (count: number, singular: string, plural: string) => (count === 1 ? singular : plural)

// Review events carry their comment count in the `pullRequestReview` arm of the
// proto `additionalInfo` oneof; non-review arms contribute nothing.
export const sumReviewComments = (events: CatchUpTimelineEvent[]) =>
  events.reduce((sum, e) => {
    const info = e.additionalInfo?.info
    return sum + (info?.case === 'pullRequestReview' ? (info.value.commentCount ?? 0) : 0)
  }, 0)

// Per-row presence predicates. Each row component uses its predicate for its null guard, and
// `hasSidebarActivity` ORs them so the empty state can never drift from what the rows render.
// Every collection is optional on the proto `CatchUpTimelineSummary` (proto3 omits empty/default
// sub-messages), so each read is optional-chained with a `0` fallback.
export const hasCommits = (s?: CatchUpTimelineSummary) => (s?.commits?.count ?? 0) > 0
export const hasBaseForce = (s?: CatchUpTimelineSummary) =>
  (s?.baseChanged?.count ?? 0) > 0 || (s?.forcePushed?.count ?? 0) > 0
export const hasApprovals = (s?: CatchUpTimelineSummary) => (s?.approvals?.count ?? 0) > 0
export const hasChangesRequested = (s?: CatchUpTimelineSummary) => (s?.changesRequested?.count ?? 0) > 0
export const hasComments = (s?: CatchUpTimelineSummary) => (s?.comments?.count ?? 0) > 0
export const hasIssueComments = (s?: CatchUpTimelineSummary) => (s?.issueComments?.count ?? 0) > 0

export const hasState = (s?: CatchUpTimelineSummary) =>
  (s?.closed?.count ?? 0) > 0 ||
  (s?.reopened?.count ?? 0) > 0 ||
  (s?.markedAsDraft?.count ?? 0) > 0 ||
  (s?.readyForReview?.count ?? 0) > 0 ||
  (s?.merged?.count ?? 0) > 0

export const hasReviewRequests = (s?: CatchUpTimelineSummary) => (s?.reviewRequested?.count ?? 0) > 0

export const hasSidebarActivity = (summary?: CatchUpTimelineSummary) =>
  hasCommits(summary) ||
  hasBaseForce(summary) ||
  hasApprovals(summary) ||
  hasChangesRequested(summary) ||
  hasComments(summary) ||
  hasIssueComments(summary) ||
  hasState(summary) ||
  hasReviewRequests(summary)
